import React, { useState, useEffect, useMemo } from "react";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Box,
  Typography,
  Checkbox,
  Tooltip,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  TextField,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  StyledDialog,
  StyledDialogContent,
  HeaderBox,
  SectionContainer,
  SectionHeader,
  SectionContent,
  CheckboxGrid,
  StyledFormControlLabel,
  SubCategoryContainer,
  SubCategoryGrid,
  StyledSubFormControlLabel,
  StyledCheckbox,
  StyledTooltip,
  CustomInputContainer,
  CustomInputItem,
  StyledTextField,
  AddRemoveButton,
  CommentContainer,
  StyledTextArea,
  DisclaimerContainer,
  ButtonContainer,
  CostSection,
  CostItem,
  TotalCostItem,
} from "@/features/realEstateDocumentFetch/components/styles";
import { CompletedScreen } from "@/features/realEstateDocumentFetch/components/CompletedScreen";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import InfoIcon from "@mui/icons-material/Info";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditLocationIcon from "@mui/icons-material/EditLocation";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RemoveIcon from "@mui/icons-material/Remove";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HomeIcon from "@mui/icons-material/Home";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { DOCUMENT_TYPES } from "../utils/index";
import { toZenkaku } from "@/utils/toZenkaku";
import { formatJPY } from "@/utils/formatJPY";
import {
  calculateEstimatedCost,
  type EstimatedCost,
} from "@/features/realEstateDocumentFetch/utils/calculateEstimatedCost";
import {
  getSubCategoryIds as getSubCategoryIdsUtil,
  getCostByDocumentId as getCostByDocumentIdUtil,
  getDocumentLabel as getDocumentLabelUtil,
} from "@/features/realEstateDocumentFetch/utils/documentTree";
import { getNextButtonDisabledReason } from "@/features/realEstateDocumentFetch/utils/getNextButtonDisabledReason";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  realEstateDocumentFormSchema,
  type RealEstateDocumentFormData,
} from "@/features/realEstateDocumentFetch/schema/realEstateDocumentFormSchema";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import { RealEstateDocumentFetcherAPI } from "../api/realEstateDocumentFetcher";
import {
  buildRequestFingerprint,
  clearPendingIdempotencyKey,
  getPendingIdempotencyKey,
} from "../utils/idempotencyKey";
import * as Sentry from "@sentry/react";
import { type RealEstateDocumentChibanKaokuNumberRow } from "../types";
import {
  SERVICE_FEE_PER_ITEM,
  MAX_CUSTOM_INPUTS,
  TOUHON_ALLOWED_EXTENSIONS,
  TOKYO_METRO_PREFECTURES,
  ADDITIONAL_LOCATION_FEE,
  POSTAL_FEE,
  NATIONWIDE_MINIMUM_FEE,
  NATIONWIDE_MINIMUM_INCLUDED_ITEMS,
} from "@/features/realEstateDocumentFetch/constants";
import { REAL_ESTATE_DOCUMENT_FETCH_MANUAL_URL } from "@/configs/constants";

interface Props {
  open: boolean;
  address: string;
  chibanList: RealEstateDocumentChibanKaokuNumberRow[];
  kaokuNumberList: RealEstateDocumentChibanKaokuNumberRow[];
  onCancel?: () => void;
  onClose?: () => void;
}

/**
 * 資料取得選択モーダル
 * @param {Props} props
 * @returns {React.FC<Props>}
 */
export const RealEstateDocumentFetchModal: React.FC<Props> = ({
  open,
  address,
  chibanList,
  kaokuNumberList,
  onCancel,
  onClose,
}) => {
  const isMobile = useIsMobile();
  const { apiClient } = useApiClient();
  // 閲覧専用ユーザーは依頼を送れない（BE も 403 で拒否する）ため、最終確認のボタンを押せなくする
  const isReadOnly = useReadOnlyUser();
  const realEstateDocumentFetchRequestAPI = new RealEstateDocumentFetcherAPI(
    apiClient
  );

  // 完了状態の管理
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [completedRequestId, setCompletedRequestId] = useState<string>("");

  // React Hook Form設定
  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    register,
    setValue,
    watch,
    reset,
    control,
  } = useForm<RealEstateDocumentFormData>({
    mode: "all",
    defaultValues: {
      address,
      buildingName: "",
      chibanList: chibanList || [],
      kaokuNumberList: kaokuNumberList || [],
      selectedDocuments: [],
      otherDocuments: [""],
      sharedOwnerNames: {},
      comment: "",
      needsPostalDelivery: false,
      postalAddress: "",
      documentLocations: {},
      hearingItems: [""],
    },
    resolver: zodResolver(realEstateDocumentFormSchema),
  });

  // 地番リスト・家屋番号リストを useFieldArray で管理
  // 値の更新は register で各 input をフォームに登録して RHF に任せる。
  // update() を毎キーストロークで呼ぶと field id が再生成されて行が remount し、
  // input の focus が外れるため使わない。
  const {
    fields: chibanFields,
    append: appendChiban,
    remove: removeChiban,
  } = useFieldArray({ control, name: "chibanList" });

  const {
    fields: kaokuFields,
    append: appendKaoku,
    remove: removeKaoku,
  } = useFieldArray({ control, name: "kaokuNumberList" });

  // フォームの値を監視
  const selectedDocuments = watch("selectedDocuments") || [];
  const otherDocuments = watch("otherDocuments") ?? [""];
  const watchedSharedOwnerNames = watch("sharedOwnerNames") ?? {};
  const comment = watch("comment") ?? "";
  const watchedAddress = watch("address") || "";
  const needsPostalDelivery = watch("needsPostalDelivery") ?? false;
  const postalAddress = watch("postalAddress") ?? "";
  const documentLocations = watch("documentLocations") ?? {};
  const hearingItems = watch("hearingItems") ?? [""];
  // chibanFields はRHFのdefault値のみ追跡するためユーザー入力を反映しない。
  // バリデーションには watch で取得したlive値を使う。
  const watchedChibanList = watch("chibanList") || [];

  // エリア種別（住所から自動判定）
  const areaType: "tokyo_metro" | "nationwide" = TOKYO_METRO_PREFECTURES.some(
    (pref) => watchedAddress.startsWith(pref)
  )
    ? "tokyo_metro"
    : "nationwide";

  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "registry",
  ]);

  // その他の書類の文字数チェック用
  const [otherDocumentsErrors, setOtherDocumentsErrors] = useState<string[]>(
    []
  );

  // 謄本ファイルアップロード用State
  const [touhonFiles, setTouhonFiles] = useState<File[]>([]);
  const [touhonFilesError, setTouhonFilesError] = useState<string>("");
  const [isTouhonDropActive, setIsTouhonDropActive] = useState<boolean>(false);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // その他の書類の文字数チェック
  useEffect(() => {
    const errors = otherDocuments.map((doc) =>
      doc && doc.length > 200
        ? "その他の書類は200文字以内で入力してください"
        : ""
    );
    setOtherDocumentsErrors(errors);
  }, [otherDocuments]);

  useEffect(() => {
    if (!open) return;
    reset({
      address,
      buildingName: "",
      chibanList: chibanList || [],
      kaokuNumberList: kaokuNumberList || [],
      selectedDocuments: [],
      otherDocuments: [""],
      sharedOwnerNames: {},
      comment: "",
      needsPostalDelivery: false,
      postalAddress: "",
    });
    // address/chibanList/kaokuNumberList は意図的に依存から外す。
    // モーダル open 時点の値をスナップショットする目的。
    // open 中に親 prop が変化しても入力中の選択状態が消えないようにする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  // ユーティリティ関数を再エクスポート（後方互換のため同名でラップ）
  const getSubCategoryIds = getSubCategoryIdsUtil;
  const getCostByDocumentId = getCostByDocumentIdUtil;
  const getDocumentLabel = getDocumentLabelUtil;

  /**
   * 概算費用（useMemo でレンダーごとに 1 回だけ計算）
   */
  const estimatedCost: EstimatedCost = useMemo(
    () =>
      calculateEstimatedCost({
        selectedDocuments,
        otherDocuments,
        hearingItems,
        documentLocations,
        needsPostalDelivery,
        areaType,
      }),
    [
      selectedDocuments,
      otherDocuments,
      hearingItems,
      documentLocations,
      needsPostalDelivery,
      areaType,
    ]
  );

  /**
   * 「次へ」ボタンの disable 理由（空文字列なら押下可能）
   */
  const nextButtonDisabledReason = useMemo(
    () =>
      getNextButtonDisabledReason({
        step,
        watchedAddress,
        localChibanList: watchedChibanList,
        selectedDocuments,
        hearingItems,
        otherDocumentsErrors,
        needsPostalDelivery,
        postalAddress,
      }),
    [
      step,
      watchedAddress,
      watchedChibanList,
      selectedDocuments,
      hearingItems,
      otherDocumentsErrors,
      needsPostalDelivery,
      postalAddress,
    ]
  );

  /**
   * セクションの展開/折りたたみを切り替える処理
   * @param sectionId - セクションのID
   */
  const handleSectionToggle = (sectionId: string): void => {
    setExpandedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  /**
   * カスタム入力項目の値を変更する処理
   * @param index - 変更する入力項目のインデックス
   * @param value - 新しい値
   */
  const handleCustomInputChange = (index: number, value: string): void => {
    const newInputs = [...otherDocuments];
    newInputs[index] = value;
    setValue("otherDocuments", newInputs);
  };

  /**
   * カスタム入力項目を追加する処理
   * 最大10個まで追加可能
   */
  const handleAddCustomInput = (): void => {
    if (otherDocuments.length < MAX_CUSTOM_INPUTS) {
      setValue("otherDocuments", [...otherDocuments, ""]);
    }
  };

  /**
   * カスタム入力項目を削除する処理
   * @param index - 削除する入力項目のインデックス
   * 最低1個は残す
   */
  const handleRemoveCustomInput = (index: number): void => {
    if (otherDocuments.length > 1) {
      // 最低1個は残す
      const newInputs = otherDocuments.filter((_, i) => i !== index);
      setValue("otherDocuments", newInputs);
    }
  };

  const handleCheckboxChange = (documentId: string): void => {
    // 親カテゴリとその子カテゴリを取得
    const parentCategory = Object.values(DOCUMENT_TYPES)
      .flatMap((section) => section.categories)
      .find((cat) => cat.subCategories?.some((sub) => sub.id === documentId));

    let newSelection: string[];

    if (parentCategory) {
      const siblingIds = getSubCategoryIds(parentCategory.id);

      if (selectedDocuments.includes(documentId)) {
        // チェックを外す場合
        newSelection = selectedDocuments.filter((id) => id !== documentId);
      } else {
        // チェックを入れる場合：同じ親カテゴリの他の子カテゴリを外してから追加（排他的選択）
        newSelection = selectedDocuments.filter(
          (id) => !siblingIds.includes(id) || id === documentId
        );
        newSelection = [...newSelection, documentId];
      }

      // すべての子カテゴリが外された場合、親カテゴリも閉じる
      const selectedSiblings = newSelection.filter((id) =>
        siblingIds.includes(id)
      );
      if (selectedSiblings.length === 0) {
        setExpandedCategories((prevExpanded) =>
          prevExpanded.filter((id) => id !== parentCategory.id)
        );
      }
    } else {
      // 通常のカテゴリ（子カテゴリを持たない）
      newSelection = selectedDocuments.includes(documentId)
        ? selectedDocuments.filter((id) => id !== documentId)
        : [...selectedDocuments, documentId];
    }

    setValue("selectedDocuments", newSelection);
  };

  const handleMainCategoryClick = (
    categoryId: string,
    hasSubCategories: boolean
  ): void => {
    if (hasSubCategories) {
      setExpandedCategories((prev) => {
        const isCurrentlyExpanded = prev.includes(categoryId);

        if (isCurrentlyExpanded) {
          // 親カテゴリを閉じる時に、その子カテゴリのチェックをすべて外す
          const subCategoryIds = getSubCategoryIds(categoryId);
          const filteredSelected = selectedDocuments.filter(
            (id) => !subCategoryIds.includes(id)
          );
          setValue("selectedDocuments", filteredSelected);

          return prev.filter((id) => id !== categoryId);
        } else {
          // 親カテゴリを開く時に、左の選択肢（最初のサブカテゴリ）を自動選択
          const subCategoryIds = getSubCategoryIds(categoryId);
          if (subCategoryIds.length > 0) {
            const firstSubCategoryId = subCategoryIds[0]; // 左の選択肢
            const filtered = selectedDocuments.filter(
              (id) => !subCategoryIds.includes(id)
            );
            setValue("selectedDocuments", [...filtered, firstSubCategoryId]);
          }

          return [...prev, categoryId];
        }
      });
    } else {
      handleCheckboxChange(categoryId);
    }
  };

  /**
   * 確定ボタンクリック時の処理
   * Step 3（料金確認）に進む前の事前チェック
   */

  const addTouhonFiles = (files: File[]): void => {
    if (files.length === 0) return;
    const invalidFiles = files.filter((file) => {
      const lowerName = file.name.toLowerCase();
      return !TOUHON_ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
    });
    if (invalidFiles.length > 0) {
      setTouhonFilesError("関連資料は PDF または ZIP のみアップロードできます");
      return;
    }
    setTouhonFilesError("");
    setTouhonFiles((prev) => {
      const next = [...prev];
      files.forEach((file) => {
        const exists = next.some(
          (f) =>
            f.name === file.name &&
            f.size === file.size &&
            f.lastModified === file.lastModified
        );
        if (!exists) next.push(file);
      });
      return next;
    });
  };

  const handleTouhonFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    addTouhonFiles(files);
  };

  const handleTouhonDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setIsTouhonDropActive(false);
    addTouhonFiles(Array.from(event.dataTransfer.files || []));
  };

  const handleRemoveTouhonFile = (targetIndex: number): void => {
    setTouhonFiles((prev) => prev.filter((_, index) => index !== targetIndex));
  };

  // 拠点情報ハンドラ
  const handleDocumentLocationChange = (docId: string, value: string): void => {
    setValue("documentLocations", { ...documentLocations, [docId]: value });
  };

  // ヒアリング項目ハンドラ
  const handleHearingItemChange = (index: number, value: string): void => {
    const next = [...hearingItems];
    next[index] = value;
    setValue("hearingItems", next);
  };

  const addHearingItem = (): void => {
    if (hearingItems.length < MAX_CUSTOM_INPUTS) {
      setValue("hearingItems", [...hearingItems, ""]);
    }
  };

  const removeHearingItem = (index: number): void => {
    setValue(
      "hearingItems",
      hearingItems.length <= 1
        ? [""]
        : hearingItems.filter((_, i) => i !== index)
    );
  };

  /**
   * フォーム送信処理（API呼び出し）
   * @param data - フォームデータ
   */
  const onSubmit = async (data: RealEstateDocumentFormData): Promise<void> => {
    try {
      const filteredOtherDocuments =
        data.otherDocuments?.filter((input) => input.trim() !== "") ?? [];

      const filteredChibanList = (data.chibanList || []).filter(
        (item) =>
          item.chibanAndKaokuNumber &&
          item.chibanAndKaokuNumber.trim() !== "" &&
          item.location &&
          item.location.trim() !== "" &&
          item.prefName &&
          item.prefName.trim() !== ""
      );
      const filteredKaokuNumberList = (data.kaokuNumberList ?? []).filter(
        (item) =>
          item.chibanAndKaokuNumber &&
          item.chibanAndKaokuNumber.trim() !== "" &&
          item.location &&
          item.location.trim() !== "" &&
          item.prefName &&
          item.prefName.trim() !== ""
      );

      const filteredHearingWithIdx = hearingItems
        .map((h, origIdx) => ({ h, origIdx }))
        .filter(({ h }) => h.trim() !== "");
      const filteredHearingItems = filteredHearingWithIdx.map(({ h }) => h);
      // hearing_* のキーを新インデックスに再マッピング
      const currentDocumentLocations = data.documentLocations ?? {};
      const hearingLocationRemap: Record<string, string> = {};
      filteredHearingWithIdx.forEach(({ origIdx }, newIdx) => {
        const loc = currentDocumentLocations[`hearing_${origIdx}`];
        if (loc?.trim()) hearingLocationRemap[`hearing_${newIdx}`] = loc;
      });
      const filteredDocumentLocations = {
        ...Object.fromEntries(
          Object.entries(currentDocumentLocations).filter(
            ([k, v]) => !k.startsWith("hearing_") && v.trim() !== ""
          )
        ),
        ...hearingLocationRemap,
      };

      const payload = {
        address: data.address,
        // 空なら送らない（任意項目）
        buildingName: (data.buildingName ?? "").trim() || undefined,
        areaType,
        chibanList: filteredChibanList,
        kaokuNumberList: filteredKaokuNumberList,
        selectedDocuments: data.selectedDocuments,
        otherDocuments: filteredOtherDocuments,
        sharedOwnerNames: data.sharedOwnerNames ?? {},
        documentLocations: filteredDocumentLocations,
        hearingItems: filteredHearingItems,
        needsPostalDelivery: data.needsPostalDelivery ?? false,
        postalAddress: data.needsPostalDelivery
          ? (data.postalAddress ?? "").trim() || undefined
          : undefined,
        comment: (data.comment ?? "").trim() || undefined,
        relatedFiles: touhonFiles,
        estimatedAmount: estimatedCost.total,
        // 取得手数料（サービス利用手数料＋拠点追加費用）は税抜で課税、実費と郵送費用は非課税として扱う
        estimatedHandlingFee:
          estimatedCost.serviceFeeTotal + estimatedCost.locationFee,
      };
      // 応答を受け取れないまま再送（画面を閉じて再依頼を含む）しても重複して作らないキー。送信内容の全項目が同じときだけ同じキーを使う
      const fingerprint = await buildRequestFingerprint(payload);

      const response = await realEstateDocumentFetchRequestAPI.submitRequest({
        ...payload,
        idempotencyKey: getPendingIdempotencyKey(fingerprint),
      });

      if (response?.requestId) {
        clearPendingIdempotencyKey(fingerprint);
        setCompletedRequestId(response.requestId);
        setIsCompleted(true);
      } else {
        toast.error("資料取得リクエストの送信に失敗しました");
      }
    } catch (error) {
      Sentry.captureException(error);
      toast.error("資料取得リクエストの送信に失敗しました");
    }
  };

  /**
   * 最終確認後の確定処理
   */
  const handleFinalConfirm = handleSubmit(
    async (data) => {
      await onSubmit(data);
    },
    (errors) => {
      Sentry.captureMessage("資料取得モーダル: バリデーション失敗", {
        level: "warning",
        extra: { errors },
      });
      toast.error("入力内容を確認してください");
    }
  );
  /**
   * キャンセルボタンクリック時の処理
   * すべての状態をリセットし、親コンポーネントのダイアログを閉じる
   */
  const handleCancel = (): void => {
    reset();
    setIsCompleted(false);
    setCompletedRequestId("");
    // sharedOwnerNames は reset() で RHF が {} にリセットするため不要
    setTouhonFiles([]);
    setTouhonFilesError("");
    // documentLocations は reset() で RHF がリセットするため不要
    // hearingItems は reset() で RHF がリセットするため不要
    // needsPostalDelivery / postalAddress は reset() で RHF がリセットするため不要
    setStep(1);
    onCancel?.(); // 親コンポーネントのダイアログを閉じる
  };

  /**
   * 完了時のクローズ処理
   */
  const handleCompletedClose = (): void => {
    reset();
    setIsCompleted(false);
    setCompletedRequestId("");
    // sharedOwnerNames は reset() で RHF が {} にリセットするため不要
    setTouhonFiles([]);
    setTouhonFilesError("");
    // documentLocations は reset() で RHF がリセットするため不要
    // hearingItems は reset() で RHF がリセットするため不要
    // needsPostalDelivery / postalAddress は reset() で RHF がリセットするため不要
    setStep(1);
    onClose?.(); // 親コンポーネントのダイアログを閉じる
  };

  return (
    <StyledDialog
      open={open}
      onClose={(_event, reason) => {
        // 送信中に閉じると、取り消したつもりの依頼が後から作成されるため、完了または失敗まで閉じない
        if (reason === "backdropClick" || isSubmitting) {
          return;
        }
        if (isCompleted) {
          handleCompletedClose();
        } else {
          handleCancel();
        }
      }}
      disableEscapeKeyDown={true}
      fullWidth={true}
      maxWidth={"xl"}
      fullScreen={isMobile}
      aria-labelledby={"real-estate-document-fetch-modal"}
      aria-describedby={"real-estate-document-fetch-modal"}
    >
      {isCompleted ? (
        // 完了画面
        <CompletedScreen
          completedRequestId={completedRequestId}
          onClose={handleCompletedClose}
        />
      ) : (
        <StyledDialogContent>
          <HeaderBox>
            <Typography
              variant="h6"
              component="h2"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
                lineHeight: 1.2,
              }}
            >
              取得依頼したい書類を選択
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => {
                  window.open(
                    REAL_ESTATE_DOCUMENT_FETCH_MANUAL_URL,
                    "_blank",
                    "noopener,noreferrer"
                  );
                }}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(59, 130, 246, 0.1)",
                    transform: "scale(1.1)",
                  },
                }}
                title="マニュアルを開く"
              >
                <OpenInNewIcon
                  sx={{
                    fontSize: { xs: "18px", sm: "20px" },
                    color: "#3b82f6",
                  }}
                />
              </IconButton>
              <IconButton
                onClick={handleCancel}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
              </IconButton>
            </Box>
          </HeaderBox>

          {/* ステップインジケーター */}
          <Box
            role="progressbar"
            aria-valuenow={step}
            aria-valuemin={1}
            aria-valuemax={3}
            aria-label="ステップ進行状況"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mb: 3,
            }}
          >
            {([1, 2, 3] as const).map((s) => (
              <Box
                key={s}
                aria-current={step === s ? "step" : undefined}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    backgroundColor: step >= s ? "#3b82f6" : "#e2e8f0",
                    color: step >= s ? "#fff" : "#94a3b8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.8rem",
                    fontWeight: 700,
                  }}
                >
                  {s}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: step === s ? "#3b82f6" : "#94a3b8",
                    fontWeight: step === s ? 700 : 400,
                    fontSize: "0.8rem",
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {s === 1 ? "書類選択" : s === 2 ? "詳細情報" : "料金確認"}
                </Typography>
                {s < 3 && (
                  <Box
                    sx={{
                      width: 32,
                      height: 2,
                      backgroundColor: step > s ? "#3b82f6" : "#e2e8f0",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>

          {step === 1 && (
            <>
              <Box
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  mb: { xs: 2, sm: 3 },
                  backgroundColor: "#f8f9fa",
                  borderRadius: "16px",
                  border: "1px solid #e9ecef",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                >
                  <PlaceIcon
                    sx={{
                      color: "#1976d2",
                      fontSize: { xs: "20px", sm: "22px" },
                      mt: 0.5,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "10px", sm: "12px" },
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      対象住所（必須）
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.82rem",
                        mb: 1.25,
                      }}
                    >
                      トップ画面からの依頼時は住所の入力が必要です
                    </Typography>
                    <StyledTextField
                      {...register("address")}
                      placeholder="必須 例：東京都渋谷区渋谷1-1-1"
                      multiline
                      minRows={2}
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "#ffffff",
                          borderRadius: "10px",
                        },
                        "& .MuiInputBase-input": {
                          fontWeight: 500,
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                          lineHeight: 1.5,
                        },
                      }}
                    />
                    <Typography
                      component="label"
                      htmlFor="real-estate-document-fetch-building-name"
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "10px", sm: "12px" },
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        display: "block",
                        mt: 2,
                        mb: 0.75,
                      }}
                    >
                      マンション名（任意）
                    </Typography>
                    <StyledTextField
                      {...register("buildingName")}
                      id="real-estate-document-fetch-building-name"
                      placeholder="例：〇〇マンション"
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                      error={errors.buildingName !== undefined}
                      helperText={errors.buildingName?.message}
                      sx={(theme) => ({
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: theme.palette.white.main,
                          borderRadius: "10px",
                        },
                        "& .MuiInputBase-input": {
                          fontWeight: 500,
                          fontSize: { xs: "0.95rem", sm: "1rem" },
                          lineHeight: 1.5,
                        },
                      })}
                    />
                  </Box>
                </Box>
              </Box>

              {/* 地番リストの選択・追加UI */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: { xs: 1.5, sm: 2 },
                  mb: { xs: 2, sm: 3 },
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9ecef",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "block",
                        mt: 0.5,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    >
                      対象地番
                    </Typography>
                  </Box>
                </Box>

                <TableContainer
                  component={Paper}
                  sx={{
                    mt: 2,
                    mb: 2,
                    borderRadius: 3,
                    boxShadow:
                      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "primary.main" }}>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountBalanceIcon fontSize="small" />
                            都道府県
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <EditLocationIcon fontSize="small" />
                            所在
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <FormatListNumberedIcon fontSize="small" />
                            地番
                          </Box>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          操作
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {chibanFields.map((row, idx) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "grey.50",
                            },
                            "&:hover": { backgroundColor: "primary.50" },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              {...register(`chibanList.${idx}.prefName`)}
                              size="small"
                              placeholder="都道府県"
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: "white",
                                  "&:hover": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "primary.main",
                                    },
                                  },
                                  "&.Mui-focused": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "primary.main",
                                      borderWidth: 2,
                                    },
                                  },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Controller
                              control={control}
                              name={`chibanList.${idx}.location`}
                              render={({
                                field: { value, onChange, onBlur, name },
                              }) => (
                                <TextField
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  onBlur={(e) => {
                                    const transformed = toZenkaku(
                                      e.target.value
                                    );
                                    if (transformed !== e.target.value) {
                                      onChange(transformed);
                                    }
                                    onBlur();
                                  }}
                                  size="small"
                                  placeholder="所在"
                                  variant="outlined"
                                  sx={{
                                    minWidth: "250px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                      backgroundColor: "white",
                                      "&:hover": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "primary.main",
                                        },
                                      },
                                      "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "primary.main",
                                          borderWidth: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Controller
                              control={control}
                              name={`chibanList.${idx}.chibanAndKaokuNumber`}
                              render={({
                                field: { value, onChange, onBlur, name },
                              }) => (
                                <TextField
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  onBlur={(e) => {
                                    const transformed = toZenkaku(
                                      e.target.value
                                    );
                                    if (transformed !== e.target.value) {
                                      onChange(transformed);
                                    }
                                    onBlur();
                                  }}
                                  size="small"
                                  placeholder="地番"
                                  variant="outlined"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                      backgroundColor: "white",
                                      "&:hover": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "primary.main",
                                        },
                                      },
                                      "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "primary.main",
                                          borderWidth: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <IconButton
                              onClick={() => {
                                removeChiban(idx);
                              }}
                              size="small"
                              color="error"
                              disabled={chibanFields.length === 1}
                              sx={{
                                backgroundColor: "error.50",
                                borderRadius: 2,
                                "&:hover": {
                                  backgroundColor: "error.100",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "grey.100",
                                  color: "grey.400",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{
                            py: 3,
                            backgroundColor: "grey.25",
                            borderTop: "2px dashed",
                            borderColor: "grey.300",
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              appendChiban({
                                prefName: "",
                                location: "",
                                chibanAndKaokuNumber: "",
                              });
                            }}
                            sx={{
                              borderRadius: 3,
                              px: 3,
                              py: 1.5,
                              borderStyle: "dashed",
                              borderWidth: 2,
                              "&:hover": {
                                borderStyle: "solid",
                                backgroundColor: "primary.50",
                              },
                            }}
                          >
                            新しい行を追加
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, px: 2 }}>
                  <Box
                    sx={{
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        fontWeight: 500,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "block",
                      }}
                    >
                      ※
                      すべての項目が入力済みの行のみ依頼対象となります（未入力項目がある行は自動で除外されます）
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* 家屋番号リストの選択・追加UI */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 2,
                  p: { xs: 1.5, sm: 2 },
                  mt: { xs: 2, sm: 2.5 },
                  mb: { xs: 2, sm: 3 },
                  backgroundColor: "#f8f9fa",
                  borderRadius: "12px",
                  border: "1px solid #e9ecef",
                  flexDirection: "column",
                  position: "relative",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 2 },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: { xs: "12px", sm: "14px" },
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "block",
                        mt: 0.5,
                        fontFamily:
                          '"Roboto", "Helvetica", "Arial", sans-serif',
                      }}
                    >
                      対象家屋番号
                    </Typography>
                  </Box>
                </Box>

                <TableContainer
                  component={Paper}
                  sx={{
                    mt: 2,
                    mb: 2,
                    borderRadius: 3,
                    boxShadow:
                      "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                    overflow: "hidden",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "secondary.main" }}>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountBalanceIcon fontSize="small" />
                            都道府県
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <EditLocationIcon fontSize="small" />
                            所在
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <HomeIcon fontSize="small" />
                            家屋番号
                          </Box>
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: 600, py: 2 }}
                        >
                          操作
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kaokuFields.map((row, idx) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:nth-of-type(odd)": {
                              backgroundColor: "grey.50",
                            },
                            "&:hover": { backgroundColor: "secondary.50" },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell sx={{ py: 2 }}>
                            <TextField
                              {...register(`kaokuNumberList.${idx}.prefName`)}
                              size="small"
                              placeholder="都道府県"
                              variant="outlined"
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 2,
                                  backgroundColor: "white",
                                  "&:hover": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "secondary.main",
                                    },
                                  },
                                  "&.Mui-focused": {
                                    "& .MuiOutlinedInput-notchedOutline": {
                                      borderColor: "secondary.main",
                                      borderWidth: 2,
                                    },
                                  },
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Controller
                              control={control}
                              name={`kaokuNumberList.${idx}.location`}
                              render={({
                                field: { value, onChange, onBlur, name },
                              }) => (
                                <TextField
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  onBlur={(e) => {
                                    const transformed = toZenkaku(
                                      e.target.value
                                    );
                                    if (transformed !== e.target.value) {
                                      onChange(transformed);
                                    }
                                    onBlur();
                                  }}
                                  size="small"
                                  placeholder="所在"
                                  variant="outlined"
                                  sx={{
                                    minWidth: "250px",
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                      backgroundColor: "white",
                                      "&:hover": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "secondary.main",
                                        },
                                      },
                                      "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "secondary.main",
                                          borderWidth: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Controller
                              control={control}
                              name={`kaokuNumberList.${idx}.chibanAndKaokuNumber`}
                              render={({
                                field: { value, onChange, onBlur, name },
                              }) => (
                                <TextField
                                  name={name}
                                  value={value}
                                  onChange={onChange}
                                  onBlur={(e) => {
                                    const transformed = toZenkaku(
                                      e.target.value
                                    );
                                    if (transformed !== e.target.value) {
                                      onChange(transformed);
                                    }
                                    onBlur();
                                  }}
                                  size="small"
                                  placeholder="家屋番号"
                                  variant="outlined"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 2,
                                      backgroundColor: "white",
                                      "&:hover": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "secondary.main",
                                        },
                                      },
                                      "&.Mui-focused": {
                                        "& .MuiOutlinedInput-notchedOutline": {
                                          borderColor: "secondary.main",
                                          borderWidth: 2,
                                        },
                                      },
                                    },
                                  }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ py: 2 }}>
                            <IconButton
                              onClick={() => {
                                removeKaoku(idx);
                              }}
                              size="small"
                              color="error"
                              disabled={kaokuFields.length === 1}
                              sx={{
                                backgroundColor: "error.50",
                                borderRadius: 2,
                                "&:hover": {
                                  backgroundColor: "error.100",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "grey.100",
                                  color: "grey.400",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          align="center"
                          sx={{
                            py: 3,
                            backgroundColor: "grey.25",
                            borderTop: "2px dashed",
                            borderColor: "grey.300",
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="medium"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              appendKaoku({
                                prefName: "",
                                location: "",
                                chibanAndKaokuNumber: "",
                              });
                            }}
                            sx={{
                              borderRadius: 3,
                              px: 3,
                              py: 1.5,
                              borderStyle: "dashed",
                              borderWidth: 2,
                              color: "secondary.main",
                              borderColor: "secondary.main",
                              "&:hover": {
                                borderStyle: "solid",
                                backgroundColor: "secondary.50",
                              },
                            }}
                          >
                            新しい行を追加
                          </Button>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, px: 2 }}>
                  <Box
                    sx={{
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                        fontWeight: 500,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        display: "block",
                      }}
                    >
                      ※
                      すべての項目が入力済みの行のみ依頼対象となります（未入力項目がある行は自動で除外されます）
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {step === 1 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: { xs: 2, sm: 3 },
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                ml: { xs: 1, sm: 2 },
              }}
            >
              取得依頼したい書類にチェックを入れてください
            </Typography>
          )}
          {step === 1 &&
            selectedDocuments.length === 0 &&
            hearingItems.every((h) => h.trim() === "") && (
              <Box
                role="alert"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fee2e2",
                  border: "1px solid #ef4444",
                  borderRadius: "10px",
                  py: 1.5,
                  px: 2,
                  mb: { xs: 2, sm: 3 },
                  mx: { xs: 1, sm: 2 },
                }}
              >
                <ErrorOutlineIcon sx={{ color: "#ef4444", mr: 1 }} />
                <Typography
                  sx={{
                    color: "#b91c1c",
                    fontWeight: 700,
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    textAlign: "center",
                  }}
                >
                  取得依頼したい書類・ヒアリング項目を1つ以上選択してください
                </Typography>
              </Box>
            )}

          {/* ===== Step 1 ===== */}
          {step === 1 && (
            <>
              {/* 免責事項 */}
              <DisclaimerContainer
                sx={{
                  mb: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    color: "#d97706",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  重要事項・免責事項
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#92400e",
                    fontSize: "0.875rem",
                    lineHeight: 1.6,
                  }}
                >
                  •
                  自治体によって取得できる資料の形式やフォーマットが異なります。資料が存在しない場合や、取得不可能な場合があります。
                  <br />
                  • 依頼〜納品まで5営業日以上かかる場合がございます。
                  <br />
                  •
                  入力の地番・家屋番号に対してすべてのご選択いただいた書類の取得を実施いたします。（例外がございましたら、備考に留意事項を記載ください）
                  <br />
                </Typography>
              </DisclaimerContainer>

              {Object.values(DOCUMENT_TYPES).map((section) => (
                <SectionContainer key={section.id} elevation={0}>
                  <SectionHeader
                    onClick={() => {
                      handleSectionToggle(section.id);
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "1.1rem", sm: "1.2rem" },
                          fontWeight: 700,
                          color: "#000000",
                          letterSpacing: "-0.025em",
                          marginBottom: "6px",
                        }}
                      >
                        {section.label}
                      </Typography>
                      <Chip
                        label={`${section.categories.length}項目`}
                        size="small"
                        sx={{
                          backgroundColor: "#e0e7ff",
                          color: "#3730a3",
                          fontWeight: 600,
                          fontSize: "11px",
                          height: "24px",
                        }}
                      />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {expandedSections.includes(section.id) ? (
                        <ExpandLessIcon
                          sx={{ fontSize: "28px", color: "#64748b" }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          sx={{ fontSize: "28px", color: "#64748b" }}
                        />
                      )}
                    </Box>
                  </SectionHeader>
                  {expandedSections.includes(section.id) && (
                    <SectionContent>
                      <CheckboxGrid>
                        {section.categories.map((category) => (
                          <Box key={category.id}>
                            <StyledFormControlLabel
                              data-checked={
                                category.subCategories
                                  ? expandedCategories.includes(category.id)
                                  : selectedDocuments.includes(category.id)
                              }
                              control={
                                <StyledCheckbox
                                  checked={
                                    category.subCategories
                                      ? expandedCategories.includes(category.id)
                                      : selectedDocuments.includes(category.id)
                                  }
                                  onChange={() => {
                                    handleMainCategoryClick(
                                      category.id,
                                      !!category.subCategories
                                    );
                                  }}
                                  icon={
                                    <Box
                                      sx={{
                                        width: 24,
                                        height: 24,
                                        border: "2px solid #cbd5e1",
                                        borderRadius: "6px",
                                        backgroundColor: "white",
                                        transition: "all 0.2s ease",
                                      }}
                                    />
                                  }
                                  checkedIcon={
                                    <CheckCircleIcon
                                      sx={{
                                        fontSize: 26,
                                        color: "#3b82f6",
                                      }}
                                    />
                                  }
                                />
                              }
                              label={
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  gap={1.5}
                                  sx={{
                                    fontSize: {
                                      xs: "0.9rem",
                                      sm: "0.95rem",
                                      md: "1rem",
                                    },
                                    lineHeight: 1.5,
                                    fontWeight: 500,
                                  }}
                                >
                                  <span
                                    style={{
                                      wordBreak: "break-word",
                                      hyphens: "auto",
                                      flex: 1,
                                      minWidth: 0,
                                    }}
                                  >
                                    {category.label}
                                  </span>
                                  {category.tooltip?.trim() && (
                                    <StyledTooltip
                                      title={category.tooltip}
                                      placement="top"
                                      arrow
                                      enterDelay={200}
                                      leaveDelay={100}
                                    >
                                      <InfoIcon
                                        sx={{
                                          fontSize: "20px",
                                          color: "#64748b",
                                          cursor: "help",
                                          flexShrink: 0,
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            color: "#3b82f6",
                                            transform: "scale(1.15)",
                                          },
                                        }}
                                      />
                                    </StyledTooltip>
                                  )}
                                  {category.explanationUrl?.trim() && (
                                    <StyledTooltip
                                      title="説明ページを開く（外部リンク）"
                                      placement="top"
                                      arrow
                                      enterDelay={200}
                                      leaveDelay={100}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          window.open(
                                            category.explanationUrl,
                                            "_blank",
                                            "noopener,noreferrer"
                                          );
                                        }}
                                        sx={{
                                          padding: "2px",
                                          color: "#64748b",
                                          flexShrink: 0,
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            color: "#3b82f6",
                                            backgroundColor:
                                              "rgba(59, 130, 246, 0.1)",
                                            transform: "scale(1.1)",
                                          },
                                        }}
                                      >
                                        <OpenInNewIcon
                                          sx={{ fontSize: "18px" }}
                                        />
                                      </IconButton>
                                    </StyledTooltip>
                                  )}
                                </Box>
                              }
                            />
                            {category.subCategories &&
                              expandedCategories.includes(category.id) && (
                                <SubCategoryContainer>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      mb: 2,
                                      fontWeight: 600,
                                      color: "#475569",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    選択してください：
                                  </Typography>
                                  <SubCategoryGrid>
                                    {category.subCategories.map(
                                      (subCategory) => (
                                        <StyledSubFormControlLabel
                                          key={subCategory.id}
                                          data-checked={selectedDocuments.includes(
                                            subCategory.id
                                          )}
                                          control={
                                            <StyledCheckbox
                                              checked={selectedDocuments.includes(
                                                subCategory.id
                                              )}
                                              onChange={() => {
                                                handleCheckboxChange(
                                                  subCategory.id
                                                );
                                              }}
                                              icon={
                                                <Box
                                                  sx={{
                                                    width: 18,
                                                    height: 18,
                                                    border: "1px solid #cbd5e1",
                                                    borderRadius: "4px",
                                                    backgroundColor: "white",
                                                    transition: "all 0.2s ease",
                                                  }}
                                                />
                                              }
                                              checkedIcon={
                                                <CheckCircleIcon
                                                  sx={{
                                                    fontSize: 20,
                                                    color: "#3b82f6",
                                                  }}
                                                />
                                              }
                                            />
                                          }
                                          label={
                                            <Box>
                                              <Typography component="span">
                                                {subCategory.label}
                                              </Typography>
                                              {(subCategory.id ===
                                                "book_land_one_part" ||
                                                subCategory.id ===
                                                  "book_building_one_part") &&
                                                selectedDocuments.includes(
                                                  subCategory.id
                                                ) && (
                                                  <Box sx={{ mt: 1 }}>
                                                    <StyledTextField
                                                      size="small"
                                                      placeholder="共有者名を入力"
                                                      value={
                                                        watchedSharedOwnerNames[
                                                          subCategory.id
                                                        ] || ""
                                                      }
                                                      onChange={(e) => {
                                                        e.stopPropagation();
                                                        const newSharedOwnerNames =
                                                          {
                                                            ...watchedSharedOwnerNames,
                                                          };
                                                        newSharedOwnerNames[
                                                          subCategory.id
                                                        ] = e.target.value;
                                                        setValue(
                                                          "sharedOwnerNames",
                                                          newSharedOwnerNames
                                                        );
                                                      }}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                      }}
                                                      sx={{
                                                        width: "100%",
                                                        mt: 0.5,
                                                        "& .MuiOutlinedInput-root":
                                                          {
                                                            height: "32px",
                                                            "& input": {
                                                              padding:
                                                                "6px 8px",
                                                              fontSize:
                                                                "0.75rem",
                                                            },
                                                          },
                                                      }}
                                                    />
                                                  </Box>
                                                )}
                                            </Box>
                                          }
                                        />
                                      )
                                    )}
                                  </SubCategoryGrid>
                                </SubCategoryContainer>
                              )}
                          </Box>
                        ))}
                      </CheckboxGrid>
                    </SectionContent>
                  )}
                </SectionContainer>
              ))}

              <CustomInputContainer>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1.1rem", sm: "1.2rem" },
                      fontWeight: 700,
                      color: "#000000",
                      letterSpacing: "-0.025em",
                      marginBottom: "6px",
                    }}
                  >
                    その他の書類
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      fontSize: "0.875rem",
                      marginBottom: "20px",
                    }}
                  >
                    上記以外で必要な書類がございましたら、最大10件までご記入ください（その他の書類のみの依頼は不可）
                  </Typography>
                </Box>

                {otherDocuments.map((input, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <CustomInputItem>
                      <StyledTextField
                        value={input}
                        onChange={(e) => {
                          handleCustomInputChange(index, e.target.value);
                        }}
                        placeholder={`その他の書類 ${index + 1}`}
                        variant="outlined"
                        size="small"
                        error={!!otherDocumentsErrors[index]}
                      />
                      <AddRemoveButton
                        onClick={() => {
                          handleRemoveCustomInput(index);
                        }}
                        disabled={otherDocuments.length === 1}
                        sx={{
                          color:
                            otherDocuments.length === 1 ? "#cbd5e1" : "#ef4444",
                          "&:hover": {
                            backgroundColor:
                              otherDocuments.length === 1
                                ? "#f1f5f9"
                                : "#fef2f2",
                          },
                        }}
                      >
                        <RemoveIcon />
                      </AddRemoveButton>
                      {index === otherDocuments.length - 1 &&
                        otherDocuments.length < MAX_CUSTOM_INPUTS && (
                          <AddRemoveButton
                            onClick={handleAddCustomInput}
                            sx={{
                              color: "#10b981",
                              "&:hover": {
                                backgroundColor: "#f0fdf4",
                              },
                            }}
                          >
                            <AddIcon />
                          </AddRemoveButton>
                        )}
                    </CustomInputItem>
                    {otherDocumentsErrors[index] && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ef4444",
                          fontSize: "0.75rem",
                          mt: 0.5,
                          ml: 2,
                        }}
                      >
                        {otherDocumentsErrors[index]}
                      </Typography>
                    )}
                  </Box>
                ))}
              </CustomInputContainer>

              {/* ヒアリング項目セクション */}
              <SectionContainer sx={{ mt: 3 }}>
                <SectionHeader>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FormatListNumberedIcon sx={{ color: "#3b82f6" }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "1.1rem", sm: "1.2rem" },
                          fontWeight: 700,
                        }}
                      >
                        ヒアリング項目
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        mt: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        役所でヒアリングしたい事項を箇条書きで入力してください（任意）
                      </Typography>
                      <Tooltip
                        title="役所のご担当者様にそのまま質問できる文言で記入されることをおすすめします"
                        placement="top"
                        arrow
                      >
                        <InfoIcon
                          sx={{
                            fontSize: 15,
                            color: "#94a3b8",
                            cursor: "help",
                            flexShrink: 0,
                          }}
                        />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#94a3b8", mt: 0.5, fontSize: "0.82rem" }}
                    >
                      ヒアリングを実施する場合でも、1項目1,000円でお受けいたします
                    </Typography>
                  </Box>
                </SectionHeader>
                <SectionContent>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {hearingItems.map((item, index) => (
                      <Box
                        key={index}
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#94a3b8", minWidth: 20 }}
                        >
                          •
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="対象物件の東側道路のセットバック状況（後退済みか）を答えてください"
                          value={item}
                          onChange={(e) => {
                            handleHearingItemChange(index, e.target.value);
                          }}
                          inputProps={{ maxLength: 200 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            removeHearingItem(index);
                          }}
                          disabled={hearingItems.length === 1 && item === ""}
                        >
                          <RemoveIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {hearingItems.length < MAX_CUSTOM_INPUTS && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          mt: 0.5,
                        }}
                      >
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={addHearingItem}
                          sx={{ color: "#3b82f6", textTransform: "none" }}
                        >
                          項目を追加
                        </Button>
                      </Box>
                    )}
                  </Box>
                </SectionContent>
              </SectionContainer>
            </>
          )}

          {/* ===== Step 2 ===== */}
          {step === 2 && (
            <>
              {/* 関連資料アップロードセクション */}
              <SectionContainer>
                <SectionHeader>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AttachFileIcon sx={{ color: "#3b82f6" }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1.1rem", sm: "1.2rem" },
                        fontWeight: 700,
                      }}
                    >
                      関連資料アップロード
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: "#64748b", mt: 0.5 }}
                  >
                    関連資料（謄本・公図など）をPDF/ZIP形式でアップロードしてください（任意・複数可）
                  </Typography>
                </SectionHeader>
                <SectionContent>
                  <Box>
                    <Box
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsTouhonDropActive(true);
                      }}
                      onDragLeave={() => {
                        setIsTouhonDropActive(false);
                      }}
                      onDrop={handleTouhonDrop}
                      sx={{
                        border: isTouhonDropActive
                          ? "2px dashed #2563eb"
                          : "2px dashed #cbd5e1",
                        backgroundColor: isTouhonDropActive
                          ? "#eff6ff"
                          : "#f8fafc",
                        borderRadius: "12px",
                        p: 3,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                      }}
                    >
                      <input
                        type="file"
                        accept=".pdf,.zip"
                        multiple
                        style={{ display: "none" }}
                        id="touhon-file-input"
                        onChange={handleTouhonFileChange}
                        aria-describedby={
                          touhonFilesError ? "touhon-file-error" : undefined
                        }
                      />
                      <label
                        htmlFor="touhon-file-input"
                        style={{ cursor: "pointer" }}
                      >
                        <CloudUploadIcon
                          sx={{
                            fontSize: 40,
                            color: isTouhonDropActive ? "#2563eb" : "#94a3b8",
                            mb: 1,
                          }}
                        />
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          クリックまたはドラッグ＆ドロップでアップロード（PDF・ZIP）
                        </Typography>
                      </label>
                    </Box>
                    {touhonFilesError && (
                      <Typography
                        id="touhon-file-error"
                        variant="body2"
                        sx={{ color: "#ef4444", mt: 1 }}
                      >
                        {touhonFilesError}
                      </Typography>
                    )}
                    {touhonFiles.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {touhonFiles.map((file, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              py: 0.5,
                            }}
                          >
                            <AttachFileIcon
                              sx={{ fontSize: 16, color: "#64748b" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ flex: 1, color: "#374151" }}
                            >
                              {file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleRemoveTouhonFile(index);
                              }}
                            >
                              <DeleteIcon
                                sx={{ fontSize: 16, color: "#ef4444" }}
                              />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </SectionContent>
              </SectionContainer>

              {/* 拠点情報セクション（書類選択後に表示） */}
              {(selectedDocuments.length > 0 ||
                hearingItems.some((h) => h.trim() !== "")) && (
                <SectionContainer>
                  <SectionHeader>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EditLocationIcon sx={{ color: "#3b82f6" }} />
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: { xs: "1.1rem", sm: "1.2rem" },
                          fontWeight: 700,
                        }}
                      >
                        取得先（拠点情報）
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "#64748b", mt: 0.5 }}
                    >
                      書類ごとに取得場所を入力してください（任意）
                    </Typography>
                    {watchedAddress && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              areaType === "tokyo_metro"
                                ? "#16a34a"
                                : "#d97706",
                            fontWeight: 600,
                          }}
                        >
                          {areaType === "tokyo_metro"
                            ? "✅ 1都3県（追加拠点費用なし）"
                            : `📍 1都3県以外（2拠点目以降 +${formatJPY(
                                ADDITIONAL_LOCATION_FEE
                              )}）`}
                        </Typography>
                      </Box>
                    )}
                  </SectionHeader>
                  <SectionContent>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {selectedDocuments.map((docId) => (
                        <Box
                          key={docId}
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ minWidth: 140, flex: 1, color: "#374151" }}
                          >
                            {getDocumentLabel(docId) || docId}
                          </Typography>
                          <TextField
                            size="small"
                            sx={{ flex: 2, minWidth: 300 }}
                            placeholder="例: 大阪市役所"
                            value={documentLocations[docId] || ""}
                            onChange={(e) => {
                              handleDocumentLocationChange(
                                docId,
                                e.target.value
                              );
                            }}
                            inputProps={{ maxLength: 200 }}
                          />
                        </Box>
                      ))}
                      {hearingItems
                        .map((item, origIdx) => ({ item, origIdx }))
                        .filter(({ item }) => item.trim() !== "")
                        .map(({ item, origIdx }) => (
                          <Box
                            key={`hearing_${origIdx}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ minWidth: 140, flex: 1, color: "#374151" }}
                            >
                              💬 {item}
                            </Typography>
                            <TextField
                              size="small"
                              sx={{ flex: 2, minWidth: 300 }}
                              placeholder="例: 大阪市役所"
                              value={
                                documentLocations[`hearing_${origIdx}`] || ""
                              }
                              onChange={(e) => {
                                handleDocumentLocationChange(
                                  `hearing_${origIdx}`,
                                  e.target.value
                                );
                              }}
                              inputProps={{ maxLength: 200 }}
                            />
                          </Box>
                        ))}
                    </Box>
                  </SectionContent>
                </SectionContainer>
              )}

              {/* 郵送・取得場所セクション */}
              <SectionContainer>
                <SectionHeader>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccountBalanceIcon sx={{ color: "#3b82f6" }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: { xs: "1.1rem", sm: "1.2rem" },
                        fontWeight: 700,
                      }}
                    >
                      郵送・取得場所
                    </Typography>
                  </Box>
                </SectionHeader>
                <SectionContent>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Checkbox
                        checked={needsPostalDelivery}
                        onChange={(e) => {
                          setValue("needsPostalDelivery", e.target.checked);
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: "#374151" }}
                        >
                          {`郵送を希望する（レターパック：${formatJPY(
                            POSTAL_FEE
                          )}）`}
                        </Typography>
                        {needsPostalDelivery && (
                          <Box sx={{ mt: 2, width: "100%" }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
                            >
                              発送場所（住所）{" "}
                              <span style={{ color: "#ef4444" }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder="例: 東京都中央区八丁堀2-14-1 住友不動産八重洲通ビル8F"
                              value={postalAddress}
                              onChange={(e) => {
                                setValue("postalAddress", e.target.value);
                              }}
                              inputProps={{ maxLength: 500 }}
                              sx={{
                                "& .MuiInputBase-root": {
                                  fontSize: "0.95rem",
                                  padding: "4px 0",
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </SectionContent>
              </SectionContainer>

              <CommentContainer>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1.1rem", sm: "1.2rem" },
                      fontWeight: 700,
                      color: "#000000",
                      letterSpacing: "-0.025em",
                      marginBottom: "6px",
                    }}
                  >
                    備考
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#64748b",
                      fontSize: "0.875rem",
                      marginBottom: "20px",
                    }}
                  >
                    追加のご要望や特記事項がございましたら、こちらにご記入ください
                  </Typography>
                </Box>

                <StyledTextArea
                  value={comment}
                  onChange={(e) => {
                    setValue("comment", e.target.value);
                  }}
                  multiline
                  placeholder="例：xxx@xxx.co.jpへの連絡希望"
                  rows={4}
                  variant="outlined"
                  error={comment.length > 1000}
                  helperText={
                    comment.length > 1000
                      ? "備考は1,000文字以内で入力してください"
                      : ""
                  }
                  sx={
                    comment.length > 1000
                      ? {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#ef4444 !important",
                            },
                            "&:hover fieldset": {
                              borderColor: "#ef4444 !important",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#ef4444 !important",
                            },
                          },
                        }
                      : {}
                  }
                />
              </CommentContainer>
            </>
          )}

          {/* ===== Step 3: 料金確認 ===== */}
          {step === 3 && (
            <CostSection sx={{ mb: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  color: "#047857",
                  mb: 2,
                }}
              >
                概算費用の確認
              </Typography>
              <CostItem
                sx={{ backgroundColor: "#f3f4f6", borderColor: "#d1d5db" }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.9rem", fontWeight: 600 }}
                >
                  実費合計
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontSize: "0.9rem", fontWeight: 600 }}
                >
                  {estimatedCost.actualCostTotal === 0
                    ? "（別途実費）"
                    : formatJPY(estimatedCost.actualCostTotal)}
                </Typography>
              </CostItem>
              {/* 1都3県は件別に¥1,000表示（合計と一致）。1都3県以外は最低発注料金を含む単一行で表示する。 */}
              {/* 件数はcalculateEstimatedCostの戻り値（documentItemCount/hearingItemCount）を参照し、フィルタ条件の二重実装を避ける。 */}
              {areaType === "tokyo_metro" && (
                <>
                  {estimatedCost.documentItemCount > 0 && (
                    <CostItem>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        {`書類サービス利用手数料（${
                          estimatedCost.documentItemCount
                        }件 × ${formatJPY(SERVICE_FEE_PER_ITEM)}）`}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                      >
                        {formatJPY(
                          estimatedCost.documentItemCount * SERVICE_FEE_PER_ITEM
                        )}
                      </Typography>
                    </CostItem>
                  )}
                  {estimatedCost.hearingItemCount > 0 && (
                    <CostItem>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        {`ヒアリング項目（${
                          estimatedCost.hearingItemCount
                        }件 × ${formatJPY(SERVICE_FEE_PER_ITEM)}）`}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                      >
                        {formatJPY(
                          estimatedCost.hearingItemCount * SERVICE_FEE_PER_ITEM
                        )}
                      </Typography>
                    </CostItem>
                  )}
                </>
              )}
              {areaType === "nationwide" &&
                estimatedCost.serviceFeeTotal > 0 && (
                  <CostItem>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                    >
                      {`書類・ヒアリング サービス利用手数料（合計${
                        estimatedCost.totalItemCount
                      }件・1都3県以外は最低 ${formatJPY(
                        NATIONWIDE_MINIMUM_FEE
                      )} / ${NATIONWIDE_MINIMUM_INCLUDED_ITEMS}件まで）`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                    >
                      {formatJPY(estimatedCost.serviceFeeTotal)}
                    </Typography>
                  </CostItem>
                )}
              {estimatedCost.locationFee > 0 && (
                <CostItem>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                  >
                    拠点追加費用（1都3県以外）
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                  >
                    ¥{estimatedCost.locationFee.toLocaleString()}
                  </Typography>
                </CostItem>
              )}
              {estimatedCost.postalFee > 0 && (
                <CostItem>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                  >
                    郵送費用（レターパック）
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                  >
                    ¥{estimatedCost.postalFee.toLocaleString()}
                  </Typography>
                </CostItem>
              )}
              <TotalCostItem>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, fontSize: "1.1rem" }}
                >
                  合計概算費用
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 700, fontSize: "1.2rem" }}
                >
                  ¥{estimatedCost.total.toLocaleString()}
                </Typography>
              </TotalCostItem>
              <Typography
                variant="body2"
                sx={{ mt: 1.5, fontSize: "0.8rem", color: "#6b7280" }}
              >
                ※
                関連資料数（PDF/ZIP）が多い場合、送信に時間がかかることがあります
              </Typography>
            </CostSection>
          )}

          <ButtonContainer>
            {/* 戻るボタン */}
            {step > 1 ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setStep((s) => (s - 1) as 1 | 2 | 3);
                }}
                disabled={isSubmitting}
                sx={{
                  minWidth: { xs: "100%", sm: "140px" },
                  height: { xs: "48px", sm: "42px" },
                  fontSize: { xs: "0.95rem", sm: "0.9rem" },
                  order: { xs: 2, sm: 1 },
                  borderRadius: "12px",
                  border: "2px solid #e2e8f0",
                  color: "#475569",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                ← 戻る
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isSubmitting}
                sx={{
                  minWidth: { xs: "100%", sm: "140px" },
                  height: { xs: "48px", sm: "42px" },
                  fontSize: { xs: "0.95rem", sm: "0.9rem" },
                  order: { xs: 2, sm: 1 },
                  borderRadius: "12px",
                  border: "2px solid #e2e8f0",
                  color: "#475569",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                キャンセル
              </Button>
            )}
            {/* 次へ / 依頼する */}
            {step < 3 ? (
              <Tooltip
                title={
                  nextButtonDisabledReason !== ""
                    ? nextButtonDisabledReason
                    : ""
                }
                placement="top"
                arrow
              >
                <span style={{ order: 1 }}>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setStep((s) => (s + 1) as 1 | 2 | 3);
                    }}
                    disabled={nextButtonDisabledReason !== ""}
                    sx={{
                      minWidth: { xs: "100%", sm: "140px" },
                      height: { xs: "48px", sm: "42px" },
                      fontSize: { xs: "0.95rem", sm: "0.9rem" },
                      fontWeight: 600,
                      textTransform: "none",
                      pointerEvents:
                        nextButtonDisabledReason !== "" ? "none" : "auto",
                    }}
                  >
                    次へ →
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Box
                sx={{
                  order: { xs: 1, sm: 2 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
                  <Button
                    variant="contained"
                    onClick={(e) => {
                      void handleFinalConfirm(e);
                    }}
                    disabled={isSubmitting || isReadOnly}
                    sx={{
                      minWidth: { xs: "100%", sm: "180px" },
                      height: { xs: "48px", sm: "42px" },
                      fontSize: { xs: "0.95rem", sm: "0.9rem" },
                      order: { xs: 1, sm: 2 },
                      fontWeight: 600,
                      textTransform: "none",
                      backgroundColor: "#059669",
                      "&:hover": { backgroundColor: "#047857" },
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1 }}
                        />
                        送信中...
                      </>
                    ) : (
                      "依頼する"
                    )}
                  </Button>
                </ReadOnlyTooltip>
              </Box>
            )}
          </ButtonContainer>
        </StyledDialogContent>
      )}
    </StyledDialog>
  );
};
